import React, { useState, useEffect } from 'react';
import { Search, MapPin, Plus, X } from 'lucide-react';

/**
 * Компонент формы поиска городов для добавления в список отслеживаемых
 */
const SearchLocationForm = ({ onAddCity }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Список популярных городов России и мира для быстрого поиска
  const popularCities = [
    { name: 'Москва', timezone: 'Europe/Moscow', country: 'Россия', flag: '🇷🇺' },
    { name: 'Санкт-Петербург', timezone: 'Europe/Moscow', country: 'Россия', flag: '🇷🇺' },
    { name: 'Екатеринбург', timezone: 'Asia/Yekaterinburg', country: 'Россия', flag: '🇷🇺' },
    { name: 'Новосибирск', timezone: 'Asia/Novosibirsk', country: 'Россия', flag: '🇷🇺' },
    { name: 'Владивосток', timezone: 'Asia/Vladivostok', country: 'Россия', flag: '🇷🇺' },
    { name: 'Калининград', timezone: 'Europe/Kaliningrad', country: 'Россия', flag: '🇷🇺' },
    { name: 'Нью-Йорк', timezone: 'America/New_York', country: 'США', flag: '🇺🇸' },
    { name: 'Лондон', timezone: 'Europe/London', country: 'Великобритания', flag: '🇬🇧' },
    { name: 'Париж', timezone: 'Europe/Paris', country: 'Франция', flag: '🇫🇷' },
    { name: 'Берлин', timezone: 'Europe/Berlin', country: 'Германия', flag: '🇩🇪' },
    { name: 'Токио', timezone: 'Asia/Tokyo', country: 'Япония', flag: '🇯🇵' },
    { name: 'Пекин', timezone: 'Asia/Shanghai', country: 'Китай', flag: '🇨🇳' },
    { name: 'Сидней', timezone: 'Australia/Sydney', country: 'Австралия', flag: '🇦🇺' },
    { name: 'Дубай', timezone: 'Asia/Dubai', country: 'ОАЭ', flag: '🇦🇪' },
    { name: 'Рио-де-Жанейро', timezone: 'America/Sao_Paulo', country: 'Бразилия', flag: '🇧🇷' },
    { name: 'Дели', timezone: 'Asia/Kolkata', country: 'Индия', flag: '🇮🇳' },
    { name: 'Стамбул', timezone: 'Europe/Istanbul', country: 'Турция', flag: '🇹🇷' },
    { name: 'Минск', timezone: 'Europe/Minsk', country: 'Беларусь', flag: '🇧🇾' },
    { name: 'Киев', timezone: 'Europe/Kiev', country: 'Украина', flag: '🇺🇦' },
    { name: 'Нур-Султан', timezone: 'Asia/Almaty', country: 'Казахстан', flag: '🇰🇿' },
  ];

  // Список временных зон России
  const russianTimezones = [
    { city: 'Калининград', timezone: 'Europe/Kaliningrad', offset: 'UTC+2 (MSK-1)' },
    { city: 'Москва, Санкт-Петербург', timezone: 'Europe/Moscow', offset: 'UTC+3 (MSK)' },
    { city: 'Самара', timezone: 'Europe/Samara', offset: 'UTC+4 (MSK+1)' },
    { city: 'Екатеринбург', timezone: 'Asia/Yekaterinburg', offset: 'UTC+5 (MSK+2)' },
    { city: 'Омск', timezone: 'Asia/Omsk', offset: 'UTC+6 (MSK+3)' },
    { city: 'Красноярск', timezone: 'Asia/Krasnoyarsk', offset: 'UTC+7 (MSK+4)' },
    { city: 'Иркутск', timezone: 'Asia/Irkutsk', offset: 'UTC+8 (MSK+5)' },
    { city: 'Якутск', timezone: 'Asia/Yakutsk', offset: 'UTC+9 (MSK+6)' },
    { city: 'Владивосток', timezone: 'Asia/Vladivostok', offset: 'UTC+10 (MSK+7)' },
    { city: 'Магадан', timezone: 'Asia/Magadan', offset: 'UTC+11 (MSK+8)' },
    { city: 'Камчатка', timezone: 'Asia/Kamchatka', offset: 'UTC+12 (MSK+9)' },
  ];

  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    // Имитация поиска в базе данных (в реальном приложении будет API запрос)
    setTimeout(() => {
      try {
        const results = popularCities.filter(city => 
          city.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          city.country.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        setSearchResults(results);
        setIsSearching(false);
        
        if (results.length === 0) {
          setSearchError(`Города "${searchTerm}" не найдено. Проверьте правильность написания.`);
        }
      } catch (error) {
        console.error('Ошибка поиска:', error);
        setSearchError('Произошла ошибка при поиске. Пожалуйста, попробуйте снова.');
        setIsSearching(false);
      }
    }, 300);
  }, [searchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      onAddCity(searchResults[0]);
      clearSearch();
    }
  };

  const handleCitySelect = (city) => {
    onAddCity(city);
    clearSearch();
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSearchError(null);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-indigo-500" />
        Добавить город
      </h2>

      <form onSubmit={handleSubmit} className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="w-5 h-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Введите название города или страны..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
              onClick={clearSearch}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </form>

      {isSearching && (
        <div className="text-center py-4">
          <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Поиск городов...</p>
        </div>
      )}

      {searchError && !isSearching && (
        <div className="text-center py-4 text-red-500 dark:text-red-400 text-sm">
          {searchError}
        </div>
      )}

      {searchResults.length > 0 && !isSearching && (
        <div className="mt-2 divide-y divide-gray-100 dark:divide-gray-700 max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
          {searchResults.map((city) => (
            <button
              key={`${city.name}-${city.country}`}
              type="button"
              className="flex items-center justify-between w-full px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
              onClick={() => handleCitySelect(city)}
            >
              <div className="flex items-center">
                <span className="text-lg mr-2">{city.flag}</span>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{city.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{city.country}</div>
                </div>
              </div>
              <div className="flex items-center text-indigo-500">
                <Plus className="w-4 h-4" />
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">Часовые пояса России</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {russianTimezones.map((item, index) => (
            <div 
              key={index} 
              className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="font-medium text-gray-900 dark:text-white">{item.city}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 flex justify-between">
                <span>{item.timezone.replace('_', ' ')}</span>
                <span className="text-indigo-500 dark:text-indigo-400">{item.offset}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchLocationForm;